import { message } from "antd";
import form from "antd/es/form";
import {
  getAuth,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  isRouteErrorResponse,
  json,
  redirect,
  useRouteError,
} from "react-router-dom";
import { auth, provider } from "~/lib";
import { INTENTS, loadCookiesData, getSafeReturnUrl, getReadyAuthUser } from "~/utils";
import { addFirebaseUser } from "~/services/server";
import { ensureUserExists } from "~/services/server/user-tenant/addUser/ensureUserExists";
import { captureAffiliateRefFromUrl, markAffiliateVisitPending, readAffiliateRefFromStorage } from "~/utils/auth/affiliateRefStorage";
import { SignUpFormValues, SignInFormValues } from "~/types";

export const SignUpPageLoader = async ({ request }: LoaderFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();

  // Capture ?ref= locally. If this is the first time we store a code (no prior
  // landing-page visit path), ask sign-in to record the visit in that same call.
  const { searchParams } = new URL(request.url);
  const existingRef = readAffiliateRefFromStorage();
  const refFromUrl = captureAffiliateRefFromUrl(request.url);
  if (refFromUrl && !existingRef) {
    markAffiliateVisitPending();
  }
  const returnUrl = searchParams.get("returnUrl");
  const safeReturnUrl = getSafeReturnUrl(returnUrl);

  // If user is already authenticated, redirect to returnUrl (or "/" if no returnUrl)
  if (user) {
    const authAccessInfo = await loadCookiesData(user?.uid || "");
    if (authAccessInfo.success) {
      return redirect(safeReturnUrl);
    }
  }

  // If not authenticated, allow access to signup page and pass returnUrl
  return { success: true, returnUrl: safeReturnUrl };
};

export const SignUpPageAction = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");

  // Read returnUrl from form data or URL search params
  const returnUrlFromForm = formData.get("returnUrl") as string | null;
  const { searchParams } = new URL(request.url);
  const returnUrlFromQuery = searchParams.get("returnUrl");
  const returnUrl = returnUrlFromForm || returnUrlFromQuery;
  const safeReturnUrl = getSafeReturnUrl(returnUrl);

  switch (intent) {
    case INTENTS._signup: {
      const payload = JSON.parse(
        formData.get("body") as string,
      ) as SignUpFormValues;

      const newUser = await addFirebaseUser(payload, true);
      if (!newUser.success) {
        throw json({ error: newUser.error, isSignUp: true }, { status: 500 });
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw json(
          { error: "Failed to get auth state after sign up", isSignUp: true },
          { status: 500 },
        );
      }
      const idToken = await currentUser.getIdToken();

      const ensureResult = await ensureUserExists(
        {
          id: newUser.data?.id!,
          email: payload.email,
          username: payload.username,
          firstName: payload.firstName,
          lastName: payload.lastName,
        },
        { idToken },
      );
      if (!ensureResult.success) {
        throw json(
          { error: ensureResult.error, isSignUp: true },
          { status: 500 },
        );
      }

      await loadCookiesData(currentUser.uid);

      return redirect(safeReturnUrl);
    }
    case INTENTS._signin: {
      try {
        const payload = JSON.parse(
          formData.get("body") as string,
        ) as SignInFormValues;
        await signInWithEmailAndPassword(auth, payload.email, payload.password);

        const user = await getReadyAuthUser();
        if (!user) {
          throw json(
            {
              error: "Failed to get auth state after sign in",
              isSignUp: false,
            },
            { status: 500 },
          );
        }
        const idToken = await user.getIdToken();
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " ",
        );
        const lastName = lastNameParts.join(" ");

        const ensureResult = await ensureUserExists(
          {
            id: user.uid,
            email: user.email || payload.email,
            username: user.email || payload.email,
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken },
        );
        if (!ensureResult.success) {
          throw json(
            { error: ensureResult.error, isSignUp: false },
            { status: 500 },
          );
        }

        await loadCookiesData(user.uid);

        message.success("Login successful!");
        return redirect(safeReturnUrl);
      } catch (error) {
        throw json({ error: error, isSignUp: false }, { status: 500 });
      }
    }
    case INTENTS._google_signin: {
      try {
        // Sign in with Google (will sign in if account exists)
        const userCredential = await signInWithPopup(auth, provider);
        if (!userCredential) {
          console.error("Error signing in with Google");
          message.error("Error signing in with Google");
          return redirect(safeReturnUrl);
        }

        const user = userCredential.user;
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " ",
        );
        const lastName = lastNameParts.join(" ");
        const idToken = await user.getIdToken();

        // Ensure user exists in DynamoDB + Firestore (Lambda /api/action/sign-in)
        const ensureResult = await ensureUserExists(
          {
            id: user.uid,
            email: user.email || "",
            username: user.email || "",
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken },
        );

        if (!ensureResult.success) {
          console.error("Failed to ensure user exists", ensureResult.error);
          message.error("Failed to complete user setup");
          throw json(
            { error: ensureResult.error, isSignUp: false },
            { status: 500 },
          );
        }

        await loadCookiesData(user.uid);

        message.success("Login successful!");
        return redirect(safeReturnUrl);
      } catch (error) {
        throw new Response(
          JSON.stringify({
            error: error,
            isSignUp: false,
          }),
          {
            headers: {
              "Content-Type": "application/json; utf-8",
            },
            status: 500,
          },
        );
      }
    }
    case INTENTS._google_signup: {
      try {
        // For sign-up, signInWithPopup will create the Firebase account if it doesn't exist
        // If the account already exists, it will just sign in
        const userCredential = await signInWithPopup(auth, provider);
        if (!userCredential) {
          console.error("Error signing up with Google");
          message.error("Error signing up with Google");
          return redirect(safeReturnUrl);
        }

        const user = userCredential.user;
        const [firstName, ...lastNameParts] = (user.displayName || "").split(
          " ",
        );
        const lastName = lastNameParts.join(" ");
        const idToken = await user.getIdToken();

        // Ensure user exists in DynamoDB + Firestore (Lambda /api/action/sign-in)
        const ensureResult = await ensureUserExists(
          {
            id: user.uid,
            email: user.email || "",
            username: user.email || "",
            firstName: firstName || "",
            lastName: lastName || "",
          },
          { idToken },
        );

        if (!ensureResult.success) {
          console.error("Failed to ensure user exists", ensureResult.error);
          message.error("Failed to complete user setup");
          throw json(
            { error: ensureResult.error, isSignUp: true },
            { status: 500 },
          );
        }

        await loadCookiesData(user.uid);

        // Send email verification for new sign-ups
        if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser);
        }

        return redirect(safeReturnUrl);
      } catch (error) {
        throw new Response(
          JSON.stringify({
            error: error,
            isSignUp: true,
          }),
          {
            headers: {
              "Content-Type": "application/json; utf-8",
            },
            status: 500,
          },
        );
      }
    }
  }
  return {};
};
